// hooks/useAuthRoles.ts
import { useEffect, useState, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { setTelemetryUserRole } from '../appInsights';

export default function useAuthRoles() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);

  const [roles, setRoles] = useState<string[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  // Super Admin: full winvoice app access + SPP data migration; no OATT
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [isUser, setIsUser] = useState<boolean>(false);
  // OATT sub-roles (assigned in Azure portal)
  const [isOattRateOwner, setIsOattRateOwner] = useState<boolean>(false);
  const [isOattBillPreparer, setIsOattBillPreparer] = useState<boolean>(false);
  const [isOattBillApprover, setIsOattBillApprover] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const hasFetched = useRef(false);
  const accountId = account?.localAccountId;

  useEffect(() => {
    if (accountId) {
      hasFetched.current = false;
    }
  }, [accountId]);

  useEffect(() => {
    // Wait for MSAL to finish initializing before concluding there is no account.
    // Without this, loading flips to false while account is still null, causing
    // route guards to redirect to /unauthorized before roles are known.
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!instance) {
      setLoading(false);
      return;
    }

    // accounts exist but useAccount() hasn't resolved yet — keep loading, wait for next render
    if (accounts.length > 0 && !account) {
      setLoading(true);
      return;
    }

    // no accounts at all — user is genuinely not logged in
    if (!account) {
      setLoading(false);
      return;
    }

    if (hasFetched.current) {
      return;
    }

    setLoading(true);

    const getAccessTokenAndRoles = async () => {
      try {
        hasFetched.current = true;

        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        const token = result.accessToken;
        setAccessToken(token);

        const expiresOn = result.expiresOn?.getTime() || Date.now() + 3600000;
        setTokenExpiresAt(expiresOn);

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
        const payload = JSON.parse(atob(base64));
        const tokenRoles: string[] = payload.roles || [];
        setRoles(tokenRoles);
        setTelemetryUserRole(tokenRoles);

        // Admin: full winvoice app access (Windvoice.Approver)
        const adminCheck = tokenRoles.includes('Windvoice.Approver');
        // Super Admin: full winvoice app access + SPP data migration (Super.admin)
        const superAdminCheck = tokenRoles.includes('Super.admin');
        // Manager: can approve but is NOT a system admin
        const managerCheck = tokenRoles.includes('Windvoice.Reporter');
        const userCheck = tokenRoles.includes('Windvoice.Preparer');

        const rateOwnerCheck = tokenRoles.includes('Windvoice.OATT.Rate.Owner');
        const billPreparerCheck = tokenRoles.includes('Windvoice.OATT.Preparer');
        const billApproverCheck = tokenRoles.includes('Windvoice.OATT.Approver');

        setIsAdmin(adminCheck);
        setIsSuperAdmin(superAdminCheck);
        setIsManager(managerCheck);
        setIsUser(userCheck);
        setIsOattRateOwner(rateOwnerCheck);
        setIsOattBillPreparer(billPreparerCheck);
        setIsOattBillApprover(billApproverCheck);

      } catch (err: any) {

        const requiresInteraction =
          err?.errorCode === 'interaction_required' ||
          err?.errorCode === 'consent_required' ||
          err?.errorCode === 'login_required' ||
          err?.errorCode === 'monitor_window_timeout' ||
          err?.errorMessage?.includes('interaction_required') ||
          err?.errorMessage?.includes('AADSTS160021');

        if (requiresInteraction) {
          // eslint-disable-next-line no-console
          console.warn('[useAuthRoles] interaction required — redirecting to logout');
          try {
            localStorage.clear();
            sessionStorage.clear();
            const all = instance.getAllAccounts();
            if (all.length > 0) {
              await instance.logoutRedirect({ account: all[0] });
              return;
            }
            globalThis.location.href = globalThis.location.origin;
            return;
          } catch {
            // Failed to clear cache
          }
        }

        hasFetched.current = false;
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsManager(false);
        setIsUser(false);
        setIsOattRateOwner(false);
        setIsOattBillPreparer(false);
        setIsOattBillApprover(false);
        setAccessToken(null);
        setTelemetryUserRole([]);
      } finally {
        setLoading(false);
      }
    };

    getAccessTokenAndRoles();
  }, [account, accounts, instance, accountId, inProgress]);

  return {
    account,
    roles,
    accessToken,
    tokenExpiresAt,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    isOattRateOwner,
    isOattBillPreparer,
    isOattBillApprover,
    loading
  };
}